import { useEffect, useState } from "react";

import type { ParseJsonResponse } from "~/workers/parse-json.worker";

import { JsonValue } from "./json-tree-viewer.utils";

export type ParsedJsonState =
  { status: "loading" } | { status: "success"; value: JsonValue; formatted?: string } | { status: "error" };

export function useParsedJson(content: string | Uint8Array, isJsonFile: boolean, format?: boolean): ParsedJsonState {
  const [parsedJsonState, setParsedJsonState] = useState<ParsedJsonState>({ status: "loading" });

  useEffect(() => {
    if (!isJsonFile) {
      setParsedJsonState({ status: "success", value: null });
      return;
    }
    setParsedJsonState({ status: "loading" });

    const worker = new Worker(new URL("~/workers/parse-json.worker.ts", import.meta.url), {
      type: "module",
    });
    const controller = new AbortController();

    worker.onmessage = (e: MessageEvent<ParseJsonResponse>) => {
      worker.terminate();
      if (controller.signal.aborted) {
        return;
      }
      if (e.data.type === "success") {
        setParsedJsonState({ status: "success", value: e.data.result, formatted: e.data.formatted });
      }
      if (e.data.type === "error") {
        setParsedJsonState({ status: "error" });
      }
    };

    // Use transferable objects for Uint8Array to avoid cloning
    if (content instanceof Uint8Array) {
      // Create a copy of the buffer to transfer (we can't transfer the original
      // as it may be used elsewhere in the app)
      const bufferCopy = content.buffer.slice(content.byteOffset, content.byteOffset + content.byteLength);
      // Transfer ownership of the buffer to the worker - no copying occurs
      worker.postMessage({ content: bufferCopy, format }, [bufferCopy]);
    } else {
      fetch(content)
        .then((response) => response.arrayBuffer())
        .then(
          (buffer) => {
            if (controller.signal.aborted) {
              return;
            }
            worker.postMessage({ content: buffer, format }, [buffer]);
          },
          () => {
            setParsedJsonState({ status: "error" });
          },
        );
    }

    return () => {
      controller.abort();
      worker.terminate();
    };
  }, [content, isJsonFile, format]);

  return parsedJsonState;
}
